import classNames from 'classnames'
import { useFormikContext } from 'formik'
import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import { ArrowDown } from 'src/assets/icons/customIcons/ArrowDown'
import PortfolioIcon from 'src/assets/icons/customIcons/menu-icons/Portfolio'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { Button } from 'src/tailwind/components/Button'
import { Section } from 'src/tailwind/components/Section'
import BackButton from 'src/views/components/BackButton'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextareaInput from 'src/views/components/forms/formik/TextareaInput'
import FormikTinyMce from 'src/views/components/forms/FormikTinyMce'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'

interface CriterionScore {
  readonly criteriaId: number
  readonly criteriaName: string
  readonly score: string
  readonly feedback: string
  readonly minScore: number
  readonly maxScore: number
  readonly studentText: string | null
}

interface CompetencyScore {
  readonly competencyId: number
  readonly competencyName: string
  readonly isIncludedInWork: boolean
  readonly criterions: CriterionScore[]
}

interface FormikValues {
  readonly comment: string
  readonly text: string
  readonly competencyScores: CompetencyScore[]
}

type ScoreOptions = Record<string, { value: string; label: string }[]>

export default function AdministratorPortfolioWorkEditPage(): JSX.Element {
  return (
    <SuspenseWrapper>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element | null {
  const t = useTranslatable()
  const { id } = useParams<{ id: string }>()
  const headers = useAuthenticatedHeaders()
  const navigate = useNavigate()
  const locale = useLocale()
  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const workId = id ?? ''
  const hasValidId = id != null && id.length > 0
  const redirectPath = `/${locale}/administrator/portfolio/works`

  const { data: work } = useApi({
    endpoint: Api.getAdministratorPortfolioWork,
    params: React.useMemo(() => ({ headers, args: { id: workId } }), [headers, workId]),
    shouldFetch: hasValidId,
  })

  // Compute scoreOptions and initialValues in a single memo
  const { scoreOptions, initialValues } = React.useMemo(() => {
    const sectionCompetencies = work?.sectionCompetencies ?? []
    const existingScores = work?.scores ?? []

    // Group workCompetencies by competencyId, preserving order for index-based text mapping
    const workCompetenciesGrouped = new Map<string, Array<{ text: string | null }>>()
    for (const wc of work?.workCompetencies ?? []) {
      const key = String(wc.competencyId)
      const existing = workCompetenciesGrouped.get(key) ?? []
      existing.push({ text: wc.text ?? null })
      workCompetenciesGrouped.set(key, existing)
    }

    const options: ScoreOptions = {}
    const competencyScores: CompetencyScore[] = []

    for (const comp of sectionCompetencies) {
      const compIdStr = String(comp.id)
      const compIdNum = typeof comp.id === 'string' ? parseInt(comp.id, 10) : comp.id
      const workCompetencyTexts = workCompetenciesGrouped.get(compIdStr) ?? []
      const isIncludedInWork = workCompetencyTexts.length > 0

      // Map all criterions for this competency
      const criterions: CriterionScore[] = (comp.criterions ?? []).map((criterion, critIndex) => {
        const criteriaIdNum = typeof criterion.id === 'string' ? parseInt(criterion.id, 10) : criterion.id
        const minScore = criterion.min ?? 0
        const maxScore = criterion.max ?? 0

        // Build score options for this criterion
        const criterionKey = `${compIdStr}-${criterion.id}`
        if (minScore <= maxScore) {
          options[criterionKey] = Array.from({ length: maxScore - minScore + 1 }, (_, i) => {
            const val = (minScore + i).toString()
            return { value: val, label: val }
          })
        } else {
          options[criterionKey] = []
        }

        // Find existing score for this competency-criterion pair
        const matchingScore = existingScores.find((s) => s.competencyId === compIdNum && s.criteriaId === criteriaIdNum)

        // Get student text for this criterion by index
        const studentText = workCompetencyTexts[critIndex]?.text ?? null

        return {
          criteriaId: criteriaIdNum,
          criteriaName: criterion.name ?? criterion.nameEn ?? '',
          score: String(matchingScore?.score ?? ''),
          feedback: matchingScore?.feedback ?? '',
          minScore,
          maxScore,
          studentText,
        }
      })

      competencyScores.push({
        competencyId: compIdNum,
        competencyName: comp.name ?? comp.nameEn ?? '',
        isIncludedInWork,
        criterions,
      })
    }

    return {
      scoreOptions: options,
      initialValues: { comment: work?.comment ?? '', text: work?.text ?? '', competencyScores },
    }
  }, [work])

  const handleSubmit = React.useCallback(
    async (values: FormikValues): Promise<void> => {
      if (!hasValidId) return

      // Flatten all criterion scores from all competencies
      const scores = values.competencyScores.flatMap((comp) =>
        comp.criterions.map((crit) => ({
          competencyId: comp.competencyId,
          criteriaId: crit.criteriaId,
          score: Number(crit.score),
          feedback: crit.feedback,
        }))
      )

      await Api.putAdministratorPortfolioWorkEvaluate({
        headers,
        args: { id: workId },
        body: {
          comment: values.comment,
          text: values.text,
          scores,
        },
      })

      toast.success(t('common:updated_successfully'))
      setIsSubmitted(true)
    },
    [t, hasValidId, workId, headers]
  )

  const breadcrumbsItems = React.useMemo(
    () => [
      { page: t('portfolio:portfolio'), path: `/${locale}/administrator/portfolio/sections` },
      { page: t('portfolio:works'), path: redirectPath },
      ...(hasValidId
        ? [{ page: work?.section?.name ?? '', path: `/${locale}/administrator/portfolio/works/edit/${workId}` }]
        : []),
    ],
    [t, locale, redirectPath, hasValidId, work?.section?.name, workId]
  )

  const sectionTitle = `${t('portfolio:portfolio')} / ${t('portfolio:works')}${hasValidId ? ` / ${work?.section?.name ?? ''}` : ''}`

  React.useEffect(() => {
    if (isSubmitted) {
      navigate(redirectPath)
    }
  }, [isSubmitted, navigate, redirectPath])

  if (work == null) {
    return null
  }

  return (
    <Section
      title={sectionTitle}
      icon={<PortfolioIcon />}
      breadcrubms={breadcrumbsItems}
      rightElement={<BackButton link={redirectPath} />}
    >
      <div className="mt-6 w-full rounded-card bg-card px-6 py-4">
        <h3 className="mb-4 text-lg font-semibold">{t('common:information')}</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <InfoItem label={t('portfolio:section')} value={work.section?.name ?? '-'} />
          <InfoItem label={t('student:student')} value={work.student?.name ?? '-'} />
          <InfoItem label={t('portfolio:mentor')} value={work.mentor?.name ?? '-'} />
        </div>
      </div>

      <Form
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleSubmit}
        classNames={{ form: 'w-full' }}
        isConfirmable={!isSubmitted}
      >
        <FormError />
        <FormContent disabled={!work.isEditable} scoreOptions={scoreOptions} />
      </Form>
    </Section>
  )
}
interface FormContentProps {
  readonly disabled?: boolean
  readonly scoreOptions: ScoreOptions
}
function FormContent({ disabled = false, scoreOptions }: FormContentProps): JSX.Element {
  const t = useTranslatable()
  const canManagePortfolio = useAdminAccess().canManage('portfolio')
  const formik = useFormikContext<FormikValues>()

  // By default all sections are open; track only the IDs that are closed
  const [closedIds, setClosedIds] = React.useState<Set<number>>(() => new Set())

  const toggleExpanded = React.useCallback((competencyId: number) => {
    setClosedIds((prev) => {
      const next = new Set(prev)
      if (next.has(competencyId)) {
        next.delete(competencyId)
      } else {
        next.add(competencyId)
      }
      return next
    })
  }, [])

  return (
    <fieldset disabled={disabled}>
      {/* Competencies Section */}
      {formik.values.competencyScores.length > 0 && (
        <div className="mt-4 w-full rounded-card bg-card px-6 py-4">
          <h3 className="mb-4 text-lg font-semibold">{t('portfolio:competencies')}</h3>
          <div className="space-y-3">
            {formik.values.competencyScores.map((competency, compIndex) => {
              const isExpanded = !closedIds.has(competency.competencyId)

              return (
                <div
                  key={competency.competencyId}
                  className={classNames('overflow-hidden rounded-card bg-card shadow-sm', {
                    'ring-2 ring-primaryRed': competency.isIncludedInWork,
                  })}
                >
                  {/* Accordion Header */}
                  <div
                    className="flex cursor-pointer items-center justify-between gap-4 bg-gradient-to-r from-card to-card/80 p-4 transition-colors hover:from-card/90"
                    onClick={() => toggleExpanded(competency.competencyId)}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={classNames(
                          'flex size-8 items-center justify-center rounded-full bg-primaryBlue/10 transition-transform duration-300',
                          isExpanded ? 'rotate-0' : '-rotate-90'
                        )}
                      >
                        <ArrowDown bold />
                      </div>
                      <div className="flex flex-col">
                        <span className="font-semibold text-primaryTextColor">{competency.competencyName}</span>
                        <span className="text-xs text-captionColor">
                          {competency.criterions.length} {t(['კრიტერიუმი', 'Criteria'])}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Accordion Content */}
                  <div
                    className={classNames(
                      'grid transition-all duration-300 ease-in-out',
                      isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                    )}
                  >
                    <div className="overflow-hidden">
                      <div className="flex flex-col gap-4 border-t border-borderGrey p-4">
                        {competency.criterions.map((criterion, critIndex) => (
                          <div key={criterion.criteriaId} className="rounded-lg border border-borderColor bg-card p-4">
                            <h5 className="mb-3 text-sm font-semibold text-primaryTextColor">
                              {criterion.criteriaName}
                            </h5>
                            <div
                              className={classNames('grid grid-cols-1 gap-4 md:grid-cols-2', {
                                'cursor-not-allowed': disabled,
                              })}
                            >
                              <SelectInput
                                name={`competencyScores.${compIndex}.criterions.${critIndex}.score`}
                                label={t('portfolio:evaluation')}
                                options={scoreOptions[`${competency.competencyId}-${criterion.criteriaId}`] ?? []}
                                disabled={disabled}
                                required={competency.isIncludedInWork}
                              />
                              <TextareaInput
                                name={`competencyScores.${compIndex}.criterions.${critIndex}.feedback`}
                                label={t('portfolio:feedback')}
                                rows={2}
                                disabled={disabled}
                                className="disabled:cursor-not-allowed"
                              />
                            </div>

                            {/* Student's description for this criterion */}
                            {criterion.studentText != null && criterion.studentText.length > 0 && (
                              <blockquote className="mt-4 overflow-hidden rounded-lg bg-borderGrey py-3 pl-4 pr-3">
                                <p className="text-secondaryTextColor mb-1 text-xs font-medium not-italic">
                                  {t(['აღწერა', 'Description'])}
                                </p>
                                <div
                                  className="text-sm italic leading-relaxed text-primaryTextColor"
                                  dangerouslySetInnerHTML={{ __html: criterion.studentText }}
                                />
                              </blockquote>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
      <div className={classNames('mt-4 w-full rounded-card bg-card px-6 py-4', { 'cursor-not-allowed': disabled })}>
        <label className="mb-[10px] flex flex-nowrap items-center pl-0 font-bold text-primaryTextColor">
          {t('portfolio:comment')} <span className="pl-1 text-lightSecondaryWarning">*</span>
        </label>
        <FormikTinyMce name="comment" required disabled={disabled} />
        <FormikTinyMce name="text" required disabled={disabled} />
      </div>

      {canManagePortfolio && (
        <div className="mt-4 flex flex-wrap justify-end">
          <Button type="submit" variant="red" disabled={!formik.isValid || !formik.dirty}>
            {t('common:save')}
          </Button>
        </div>
      )}
    </fieldset>
  )
}

interface InfoItemProps {
  readonly label: string
  readonly value: string
}

const InfoItem = React.memo(function InfoItem({ label, value }: InfoItemProps): JSX.Element {
  return (
    <div className="flex flex-col">
      <span className="font-bold text-primaryTextColor">{label}:</span>
      <span className="text-primaryTextColor">{value}</span>
    </div>
  )
})
